import type { BaseCoreGameCommunicationProcessor } from '../../controllers/gameCommunication/processors/BaseCoreGameCommunicationProcessor';
import { MessageName } from '../../controllers/gameCommunication/types';
import { type SOCKET_CONNECTION_STATUS } from '../../services/webSocket/types';
import type { CoreStoreType } from '../../store/CoreStore';

import type { SocketCredentialsStrategy } from './credentials/types';

export type MessageNameValue = (typeof MessageName)[keyof typeof MessageName];

export interface BaseGameCommunicationMessageType extends Record<string, unknown> {
  beforeInit: boolean;
  meta: {
    delay: number;
  };
  seq: number;
  messageName: MessageNameValue;
}

export interface GameMessage extends BaseGameCommunicationMessageType {
  messageName: typeof MessageName.Game;
  id: number;
}

export interface SessionMessage extends BaseGameCommunicationMessageType {
  messageName: typeof MessageName.Session;
}

export interface GameCommunicationToasterType extends BaseGameCommunicationMessageType {
  messageName: typeof MessageName.ToasterMessage;
  code: string;
  tableId: string;
}

export interface UnknownMessage extends BaseGameCommunicationMessageType {
  messageName: Exclude<
    MessageNameValue,
    typeof MessageName.Game | typeof MessageName.Session | typeof MessageName.ToasterMessage
  >;
}

export interface GameCommunicationControllerConfig {
  processors?: IGameCommunicationProcessorFactory[];
  senders?: IGameCommunicationSender<unknown>[];
  connectionObserver?: IGameCommunicationConnectionObserver;
  credentialStrategy?: SocketCredentialsStrategy;
}

export interface GameCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
  config?: GameCommunicationControllerConfig;
}
export type GameCommunicationMessageType = GameMessage | SessionMessage | GameCommunicationToasterType | UnknownMessage;

export interface ExtractedMessageType {
  amount: number;
  betCode: number;
  bettype: string;
  nc: number;
}
export type GameCommunicationSenderType =
  | ((data: string | ArrayBufferLike | Blob | ArrayBufferView) => void)
  | undefined;

export type GameConnectionStatus = SOCKET_CONNECTION_STATUS | GAME_STATUS;

export interface GameCommunicationMetaType {
  connectionStatus: GameConnectionStatus;
  connectionProcedure: () => void;
}

export type IGameCommunicationProcessorFactory = new (store: CoreStoreType) => BaseCoreGameCommunicationProcessor;

export interface IGameCommunicationSender<Payload> {
  name: string;
  dispatch(payload: Payload): void;
  send(payload: Payload, store: CoreStoreType, sender: GameCommunicationSenderType): void;
}

export interface IGameCommunicationConnectionObserver {
  update(connectedStatus: GameConnectionStatus, store: CoreStoreType): void;
}

export interface GameCommunicationTimeout {
  timeout: number;
  messageName: string;
  process: () => void;
  seq: number;
  queuedGameId: string | null;
}

export enum GAME_STATUS {
  NEW_ROUND,
}
